import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';

import {
  AccionPrepararOrden,
  CONFIGURACION_FICHA_PREPARAR_ORDEN,
  CONFIGURACION_FORMULARIO_PREPARAR_CON_DOMICILIO,
  CONFIGURACION_FORMULARIO_PREPARAR_ORDEN,
  OPCIONES_MOTIVO_APLAZAR,
  OPCIONES_MOTIVO_RECHAZO,
} from './utilidades/preparar-orden.static';
import { deepCopy } from 'src/app/compartido/utilidades/objetos.utils';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import { OrdenesService } from '../../utilidades/ordenes.service';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Orden } from '../../utilidades/orden.class';
import {
  BehaviorSubject,
  firstValueFrom,
  map,
  Subject,
  Subscription,
} from 'rxjs';
import { ResponsiveService } from 'src/app/nucleo/utilidades/responsive.service';
import {
  EstadoOrden,
  OrdenCrearDTO,
  TipoPreparacion,
} from '../../utilidades/ordenes.models';
import { ESTADOS_ORDEN_PREPARADA } from '../../utilidades/ordenes.static';
import { valorNumérico } from 'src/app/compartido/utilidades/valores.utils';
import { FORMATO_FECHA_PIPE } from 'src/app/compartido/utilidades/compartido.static';
import {
  ConfiguracionFormulario,
  OpcionCampo,
  TipoCampoFormulario,
  ValoresFormulario,
} from 'src/app/compartido/formulario/utilidades/formulario.models';
import { obtenerCampo } from 'src/app/compartido/formulario/utilidades/formulario.utils';
import {
  CONFIGURACION_FORMULARIO_FORMULA_ORDEN,
  CONFIGURACION_FORMULARIO_ITEM_ORDEN,
} from '../../paginas/crear-orden/utilidades/crear-orden.static';
import { DialogosService } from 'src/app/nucleo/utilidades/dialogos.service';
import { Permiso } from 'src/app/nucleo/autorizacion/autorizacion.models';

@Component({
  templateUrl: './preparar-orden.component.html',
  styleUrls: ['./preparar-orden.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PrepararOrdenComponent implements OnInit {
  /* Suscripción */
  private _suscripcion = new Subscription();

  /* ID de la orden */
  @Input() public idOrden: number | undefined;

  /* Emite si la orden se ha preparado */
  @Output() protected readonly preparada = new EventEmitter<boolean>();

  /* Orden */
  protected orden: Orden | undefined;

  /* Configuración ficha */
  protected configFicha = deepCopy(CONFIGURACION_FICHA_PREPARAR_ORDEN);

  /* Formulario */
  protected configFormulario = deepCopy(
    CONFIGURACION_FORMULARIO_PREPARAR_ORDEN,
  );
  protected valorFormulario: ValoresFormulario = {};
  protected validezFormulario = false;

  /* Formulario Domicilio */
  protected configFormularioDomicilio = deepCopy(
    CONFIGURACION_FORMULARIO_PREPARAR_CON_DOMICILIO,
  );
  protected valorFormularioDomicilio: ValoresFormulario = {};
  protected validezFormularioDomicilio = false;

  /* Formularios orden sucesora */
  protected configFormulariosItems: ConfiguracionFormulario[] = [];
  protected valoresFormulariosItems: ValoresFormulario[] = [];
  protected validezFormulariosItems: boolean[] = [];
  protected get validezItems(): boolean {
    return (
      this.validezFormulariosItems.length > 0 &&
      this.validezFormulariosItems.every((validez) => validez)
    );
  }

  /* Formulario de la fórmula de la orden sucesora */
  protected configFormularioFormula = deepCopy(
    CONFIGURACION_FORMULARIO_FORMULA_ORDEN,
  );
  protected valorFormularioFormula: ValoresFormulario = {};
  protected validezFormularioFormula = false;

  /* Constantes */
  protected readonly Permiso = Permiso;
  protected readonly EstadoOrden = EstadoOrden;
  protected readonly FORMATO_FECHA_PIPE = FORMATO_FECHA_PIPE;
  protected readonly AccionPrepararOrden = AccionPrepararOrden;

  /* Estado de carga */
  protected cargando = signal(true);

  constructor(
    private readonly _alertasService: AlertasService,
    private readonly _ordenesService: OrdenesService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _dialogosService: DialogosService,
    protected readonly responsiveService: ResponsiveService,
  ) {}

  ngOnInit(): void {
    // Obtener la orden
    firstValueFrom<number>(
      this._route.params.pipe(
        map((params: Params) => valorNumérico(params['id'])!),
      ),
    ).then((id: number | undefined) => {
      const idOrden = this.idOrden ?? id;
      this._ordenesService
        .obtenerOrden$(idOrden!)
        .then((orden: Orden) => {
          this.orden = orden;
          this._configurarFormulario();
          this.cargando.set(false);
          this._cdr.markForCheck();
        })
        .catch((error: ErrorFrontend) => {
          this._alertasService.alertarError('Error: ' + error.message);
          this.cargando.set(false);
        });
    });
  }

  ngOnDestroy(): void {
    this._suscripcion.unsubscribe();
  }

  /**
   * Configura el formulario de preparación de la orden.
   * @author Juan Corral
   */
  private _configurarFormulario(): void {
    // Obtener campos
    const campoAccion = obtenerCampo(this.configFormulario, 'accion')!;
    if (campoAccion.tipo !== TipoCampoFormulario.OPCION_MULTIPLE)
      throw new Error('El campo acción debe ser de tipo OPCION_MULTIPLE');
    const campoMotivo = obtenerCampo(this.configFormulario, 'motivo')!;
    if (campoMotivo.tipo !== TipoCampoFormulario.OPCION_MULTIPLE)
      throw new Error('El campo motivo debe ser de tipo OPCION_MULTIPLE');
    const campoObservacion = obtenerCampo(
      this.configFormulario,
      'observacion',
    )!;
    const campoAplazar = obtenerCampo(this.configFormulario, 'aplazar_hasta')!;

    // Asignar observables a campos
    campoAccion.valorOut = new Subject<string>();
    campoMotivo.valorIn = new Subject<string>();
    campoMotivo.requeridoIn = new Subject<boolean>();
    campoMotivo.opcionOut = new Subject<OpcionCampo<string | number>>();
    campoObservacion.valorIn = new Subject<string>();
    campoAplazar.valorIn = new Subject<string>();
    campoAplazar.requeridoIn = new Subject<boolean>();

    // Escuchar acción para mostrar campos condicionales
    const escucharAccion = campoAccion.valorOut.subscribe((valor: string) => {
      // Aplicar configuración base
      campoMotivo.oculto = true;
      campoMotivo.requeridoIn!.next(false);
      campoMotivo.valorIn!.next(null);
      campoObservacion.oculto = false;
      campoObservacion.valorIn!.next(null);
      campoAplazar.oculto = true;
      campoAplazar.requeridoIn!.next(false);
      campoAplazar.valorIn!.next(null);
      this._configurarFormularioDomicilio();

      // Configurar campos condicionales
      switch (valor) {
        case AccionPrepararOrden.APLAZAR:
          campoMotivo.oculto = false;
          campoMotivo.requeridoIn!.next(true);
          campoMotivo.opciones = OPCIONES_MOTIVO_APLAZAR;
          campoAplazar.oculto = false;
          campoAplazar.requeridoIn!.next(true);
          break;
        case AccionPrepararOrden.RECHAZAR:
          campoMotivo.oculto = false;
          campoMotivo.requeridoIn!.next(true);
          campoMotivo.opciones = OPCIONES_MOTIVO_RECHAZO;
          break;
      }
      this._cdr.markForCheck();
    });
    this._suscripcion.add(escucharAccion);

    // Escuchar motivo para poblar observación
    const escucharMotivo = campoMotivo.opcionOut.subscribe(
      (valor: OpcionCampo<string | number>) =>
        campoObservacion.valorIn!.next(valor.extra),
    );
    this._suscripcion.add(escucharMotivo);

    // CASO ESPECIAL para Evedisa Pereira
    if (this.orden!.cliente.organizacion.alias === 'EP') {
      campoAccion.valorIn = new BehaviorSubject<AccionPrepararOrden>(
        AccionPrepararOrden.PREPARAR,
      );
      campoAccion.oculto = true;
    }
  }

  /**
   * Configura el formulario de domicilio de la orden.
   * @author Juan Corral
   */
  private _configurarFormularioDomicilio(): void {
    // Reiniciar formulario
    this.configFormularioDomicilio = deepCopy(
      CONFIGURACION_FORMULARIO_PREPARAR_CON_DOMICILIO,
    );

    // Obtener campos
    const campoCrear = obtenerCampo(this.configFormularioDomicilio, 'crear')!;
    const campoMotivoDomicilio = obtenerCampo(
      this.configFormularioDomicilio,
      'motivo',
    )!;
    const campoRecaudoDomicilio = obtenerCampo(
      this.configFormularioDomicilio,
      'recaudo',
    )!;

    // Asignar observables a campos
    campoCrear.valorOut = new Subject<boolean>();
    campoMotivoDomicilio.valorIn = new Subject<string>();
    campoMotivoDomicilio.requeridoIn = new Subject<boolean>();
    campoRecaudoDomicilio.valorIn = new Subject<string>();

    // Escuchar crear para mostrar campos condicionales
    const escucharCrear = campoCrear.valorOut.subscribe((valor: boolean) => {
      campoMotivoDomicilio.oculto = valor;
      campoMotivoDomicilio.requeridoIn!.next(!valor);
      campoMotivoDomicilio.valorIn!.next(null);
      campoRecaudoDomicilio.oculto = !valor;
      campoRecaudoDomicilio.valorIn!.next(0);
      this._cdr.markForCheck();
    });
    this._suscripcion.add(escucharCrear);
  }

  /**
   * Agrega un formulario de item a la lista de formularios de items de la orden.
   * @author Juan Corral
   */
  protected agregarItem(): void {
    const configuracion = deepCopy(CONFIGURACION_FORMULARIO_ITEM_ORDEN);
    this.configFormulariosItems.push(configuracion);
    this.valoresFormulariosItems.push({});
    this.validezFormulariosItems.push(false);
    this._cdr.markForCheck();
  }

  /**
   * Elimina un formulario de item de la lista de formularios de items de la orden.
   * @param {number} index - Índice del formulario a eliminar.
   * @author Juan Corral
   */
  protected eliminarItem(index: number): void {
    this.configFormulariosItems.splice(index, 1);
    this.valoresFormulariosItems.splice(index, 1);
    this.validezFormulariosItems.splice(index, 1);
    this._cdr.markForCheck();
  }

  /**
   * Realiza la acción correspondiente.
   * @author Juan Corral
   */
  protected realizarAccion(): void {
    // Acción de preparación
    const accion = this.valorFormulario['accion'];

    // Realizar acción
    switch (accion) {
      case AccionPrepararOrden.PREPARAR:
        this.prepararOrden();
        break;
      case AccionPrepararOrden.PREPARAR_PARCIAL:
        this.prepararOrdenParcialmente();
        break;
      case AccionPrepararOrden.APLAZAR:
        this.aplazarPreparacion();
        break;
      case AccionPrepararOrden.RECHAZAR:
        this.rechazarOrden();
        break;
    }
  }

  /**
   * Prepara la orden.
   * @author Juan Corral
   */
  protected prepararOrden(): void {
    this.cargando.set(true);

    // Datos de preparación
    const accion = this.valorFormulario['accion'];
    const observacion = this.valorFormulario['observacion'];
    const sinDomicilio = !this.valorFormularioDomicilio['crear'];
    const recaudo = valorNumérico(this.valorFormularioDomicilio['recaudo']);
    const motivoSinDomicilio = this.valorFormularioDomicilio['motivo'];

    // Definir tipo de preparación
    const tipo =
      accion === AccionPrepararOrden.PREPARAR
        ? TipoPreparacion.TOTAL
        : TipoPreparacion.PARCIAL;

    // Preparar orden
    this._ordenesService
      .prepararOrden$(
        this.orden!.id,
        tipo,
        observacion,
        sinDomicilio,
        recaudo,
        motivoSinDomicilio,
      )
      .then((orden: Orden) => {
        this._dialogosService
          .abrirDialogo$(
            '¡Orden Preparada!',
            `- La orden ${orden.id} ha sido preparada con éxito.` +
              (orden.sucesora
                ? `\n- Se ha creado una orden sucesora con id ${orden.sucesora} para los items que quedaron pendientes.`
                : ''),
          )
          .then(async () => {
            // Imprimir etiqueta, si se requiere
            const por_hoja = orden.origen?.etiquetas_por_hoja ?? 0;
            if (!orden.sinDomicilio && por_hoja === 1) {
              const imprimir = await this._dialogosService.abrirDialogo$(
                '¿Imprimir la etiqueta de la orden?',
                'Puedes imprimir la etiqueta de la orden ahora o más tarde.',
                'Imprimir',
                'Más tarde',
              );
              if (imprimir)
                this._ordenesService.descargarEtiquetas$({
                  ids: orden.id.toString(),
                });
            }

            this.preparada.emit(true);
            if (!this.idOrden) this._router.navigate(['ordenes']);
          });
      })
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError(error.message);
        this.cargando.set(false);
        this._cdr.markForCheck();
      });
  }

  /**
   * Prepara parcialmente la orden, creando una orden sucesora con los items y fórmulas seleccionados.
   * @author Juan Corral
   */
  protected prepararOrdenParcialmente(): void {
    this.cargando.set(true);

    // Construir orden sucesora
    const orden: OrdenCrearDTO = {
      enumeracion: undefined,
      referencia: this.orden!.referencia ?? undefined,
      tipo: this.orden!.tipo,
      prioridad: this.orden!.prioridad,
      origen: this.orden!.origen?.id ?? undefined,
      centro_costo: this.orden!.centroCosto ?? undefined,
      servicio: this.orden!.servicio ?? 'Pendiente',
      recaudo: this.orden!.recaudo,
      direccion: {
        direccion: this.orden!.direccion.direccion,
        direccion_formateada:
          this.orden!.direccion.direccion_formateada ?? undefined,
        detalles: this.orden!.direccion.detalles ?? undefined,
        barrio: this.orden!.direccion.barrio ?? undefined,
        localidad: this.orden!.direccion.localidad ?? undefined,
        ciudad: this.orden!.direccion.ciudad ?? undefined,
        departamento: this.orden!.direccion.departamento ?? undefined,
        latitud: this.orden!.direccion.latitud ?? undefined,
        longitud: this.orden!.direccion.longitud ?? undefined,
        creacion_manual: true,
      },
      cliente: {
        tipo_identificacion: this.orden!.cliente.tipo_identificacion,
        identificacion: this.orden!.cliente.identificacion,
        nombre: this.orden!.cliente.nombre,
        telefono: this.orden!.cliente.telefono ?? undefined,
        celular: this.orden!.cliente.celular ?? undefined,
        correo: this.orden!.cliente.correo ?? undefined,
        organizacion: this.orden!.cliente.organizacion.id,
      },
      items: this.validezItems
        ? this.valoresFormulariosItems.map((valoresItem) => ({
            producto: {
              nombre: valoresItem['producto'],
              id_externo: undefined,
            },
            cantidad: valoresItem['cantidad'],
          }))
        : [],
      formulas: this.validezFormularioFormula
        ? [
            {
              referencia: this.valorFormularioFormula['referencia'],
              archivo: this.valorFormularioFormula['archivo'].files[0] as File,
            },
          ]
        : [],
      notas: this.orden!.notas ?? undefined,
      antecesora: this.orden!.id,
    };

    // Crear orden sucesora
    this._ordenesService
      .crearOrden$(orden, true)
      .then(() => this.prepararOrden())
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError(error.message);
        this.cargando.set(false);
        this._cdr.markForCheck();
      });
  }

  /**
   * Aplaza la preparación de la orden.
   * @author Juan Corral
   */
  protected aplazarPreparacion(): void {
    this.cargando.set(true);

    // Datos de aplazamiento
    const aplazarHasta = this.valorFormulario['aplazar_hasta'];
    const motivo = this.valorFormulario['motivo'];
    const observacion = this.valorFormulario['observacion'];

    // Preparar orden
    this._ordenesService
      .aplazarPreparacionOrden$(
        this.orden!.id,
        aplazarHasta,
        motivo,
        observacion,
      )
      .then(() => {
        this._alertasService.alertarExito('Preparación aplazada con éxito');
        this.preparada.emit(false);
        if (!this.idOrden) this._router.navigate(['ordenes']);
      })
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError(error.message);
        this.cargando.set(false);
        this._cdr.markForCheck();
      });
  }

  /**
   * Rechaza la orden.
   * @author Juan Corral
   */
  protected rechazarOrden(): void {
    this.cargando.set(true);

    // Datos de rechazo
    const motivo = this.valorFormulario['motivo'];
    const observacion = this.valorFormulario['observacion'];

    // Rechazar orden
    this._ordenesService
      .rechazarOrden$(this.orden!.id, motivo, observacion)
      .then(() => {
        this._alertasService.alertarExito('Orden rechazada con éxito');
        this.preparada.emit(false);
        if (!this.idOrden) this._router.navigate(['ordenes']);
      })
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError(error.message);
        this.cargando.set(false);
        this._cdr.markForCheck();
      });
  }
}
